import type { ObservabilityWindow } from "@/api";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { cn } from "@/lib/utils";

interface WindowSelectorProps {
  value: ObservabilityWindow;
  onChange: (value: ObservabilityWindow) => void;
  /** Subset of windows to expose. Default = all four. */
  windows?: ReadonlyArray<ObservabilityWindow>;
}

// Each window maps to a translation key so the selector re-renders
// on a locale switch.
const WINDOW_KEY: Record<ObservabilityWindow, TranslationKey> = {
  "1h": "observability.windows.1h",
  "24h": "observability.windows.24h",
  "7d": "observability.windows.7d",
  "30d": "observability.windows.30d",
};

const ALL_WINDOWS: ReadonlyArray<ObservabilityWindow> = [
  "1h",
  "24h",
  "7d",
  "30d",
];

export function WindowSelector({
  value,
  onChange,
  windows = ALL_WINDOWS,
}: WindowSelectorProps) {
  const { t } = useTranslation();

  return (
    <div className="inline-flex gap-1 rounded-md border border-ocean-100 bg-ocean-50/30 p-1">
      {windows.map((w) => {
        const isActive = w === value;
        return (
          <button
            key={w}
            type="button"
            onClick={() => onChange(w)}
            aria-pressed={isActive}
            className={cn(
              "rounded-md px-3 py-1 text-xs font-medium transition-colors",
              isActive
                ? "bg-white text-ocean-900 shadow-sm"
                : "text-neutral-600 hover:bg-white/60 hover:text-ocean-800"
            )}
          >
            {t(WINDOW_KEY[w])}
          </button>
        );
      })}
    </div>
  );
}
